import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { useModals } from "ModalProvider";
import { useSearchParams } from "react-router-dom";
import { searchByProp } from "utils/search";
import { OS_PLAN_STOCK_OVERVIEW } from "./graphql";
import ProductStockDetail from "./ProductStockDetail";

const OSStockOverview = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());
  const q = params.q || "";
  const filter = params.filter || "ALL";

  const modal = useModals();

  const { loading, error, data } = useQuery(OS_PLAN_STOCK_OVERVIEW, {
    fetchPolicy: "network-only",
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const products = JSON.parse(data.osPlanStockOverview);
  const latestRows = Object.values(products)
    .map((product) => ({
      id: product.id,
      odoo_id: product.odoo_id,
      number: product.number,
      name: product.name,
      row: product.rows?.[product.rows.length - 1] ?? null,
    }))
    .filter((i) => i.row)
    .filter((i) => searchByProp(i, ["number", "name"], q))
    .filter((i) =>
      filter === "HAS_NEGATIVE"
        ? ["ab_cumsum", "ltd_cumsum", "ss_cumsum"].some(
            (key) => Number(i.row[key]) < 0,
          )
        : true,
    );

  function showDetail(productId) {
    const product = products[productId];
    modal.present({
      title: product.number,
      subtitle: "Recent 20 Stock overview",
      maxWidth: "max-w-7xl",
      children: <ProductStockDetail product={product} />,
    });
  }

  return (
    <Page title="OS Plan Stock Overview" className="h-screen" backTo="./..">
      <div className="overflow-scroll flex flex-col p-6">
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <SearchBar
              value={q}
              initialQuery={q}
              placeholder="Search by product number or name"
              onChange={(newQ) => setSearchParams({ ...params, q: newQ })}
            />
          </div>
          <div>
            <Select
              value={filter}
              onChange={(e) =>
                setSearchParams({ ...params, filter: e.target.value })
              }
            >
              <option value="ALL">All Products</option>
              <option value="HAS_NEGATIVE">Has Negative Stock</option>
            </Select>
          </div>
        </div>

        <div className="flex-1 overflow-auto mt-4">
          <div className="card p-0">
            <table className="text-right">
              <thead>
                <tr className="sticky top-0 z-20 bg-gray-200">
                  <th className="text-left">Product</th>
                  <th className="text-right">AB</th>
                  <th className="text-right">LTD</th>
                  <th className="text-right">SS</th>
                </tr>
              </thead>
              <tbody>
                {latestRows.map((i) => (
                  <tr
                    key={i.id}
                    className="border-y border-gray-100 dark:border-gray-700"
                  >
                    <td className="text-left">
                      <Button onClick={() => showDetail(i.odoo_id)}>
                        [{i.number}] {i.name}
                      </Button>
                    </td>
                    <td>{i.row.ab_cumsum}</td>
                    <td>{i.row.ltd_cumsum}</td>
                    <td>{i.row.ss_cumsum}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default OSStockOverview;
